import React, { useRef, useCallback, useMemo, Fragment, useState, useEffect } from 'react';
import { Row, Col, Nav, NavItem, NavLink, Spinner, Button } from 'reactstrap';
import { useQuery, useInfiniteQuery, useQueryClient } from 'react-query';
import { Slate } from 'slate-react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import clsx from 'clsx';

import {
    FrontDeskProvider,
    useFrontDeskState,
    useFrontDeskDispatch,
    CHANGE_TAB,
    SET_PREVIEW_MODE,
    TOGGLE_SET_DETAIL_OPEN,
    useFrontDesk,
    SET_CONTRACT,
    SET_SEARCH
} from 'context/frontdesk';
import { fetchContract, fetchContractList } from 'api/actions/contracts';
import useIntersectionObserver from 'hooks/useIntersectionObserver';
import ContractDetailContainer from '../ContractDetail/ContractDetail';
import { Contract, ContractItem } from './Contracts/Contracts';
import { TemplateItem, Template } from './Templates/Templates';
import { fetchTemplates } from 'api/actions/templates';
import { fetcher } from 'api/client';
import { MainHeader } from 'components/Shared/MainHeader';
import { TratoFormat, getEditorInstance } from 'components/Editor/Canvas/TratoFormat';
import { useConstant } from 'hooks/useConstant';
import { useParams } from 'react-router-dom';
import { agendaFields } from 'components/Modals/AgendaModal'
import useDebounce from 'hooks/useDebounce';
import { EditorProvider } from 'components/Editor/editor-context';
import { ActionsToolBar } from 'components/Editor/Toolbar/Toolbar';

function Loading() {
    const { t } = useTranslation();
    return (
        <div className="mt-5 text-center">
            <span>
                <Spinner color="primary" className="mr-2" size="sm" />{t('frontDesk.loading')}
            </span>
        </div>
    );
}

function FrontDeskBaseElement({ children }) {
    return (
        <div className="p-2">
            {children}
        </div>
    )
}

function PreviewMode() {

    const { t, i18n } = useTranslation();
    const [{ preview_mode, contract }, dispatch] = useFrontDesk();
    const handleReturn = useCallback(() => dispatch({ type: SET_PREVIEW_MODE, preview_mode: false }), [dispatch]);
    const wordonline = useMemo(() => contract?.wordonline || {}, [contract?.wordonline]);
    const currentVersion = wordonline?.versions?.find((v) => v.versionId === wordonline.Version);
    const url = currentVersion?.url;
    const editor = useConstant(() => getEditorInstance({}));
    let uilang = 'es-mx';
    switch (i18n.language) {
        case 'en':
            uilang = 'en-us';
            break;
        case 'pt':
            uilang = 'pt-br';
            break;
        case 'de':
            uilang = 'de-de';
            break;
        default:
            uilang = 'es-mx';
            break;
    }
    const documentType = useMemo(() => {
        if (contract?.importedPdf) return 'pdf';
        else if (Object.keys(wordonline).length) return 'word';
        return 'html';
    }, [contract?.importedPdf, wordonline]);

    if (!preview_mode) return null;
    return (
        <div className="d-flex flex-column h-100 overflow-hidden">
            <div className="overflow-hidden d-flex flex-column h-100">
                <div>
                    <h5 className="font-weight-bold">{contract.name} </h5>
                    {t('frontDesk.preview')}
                </div>
                <div className="overflow-hidden h-100" style={{ background: '#a5a5a5' }}>
                    {
                        {
                            pdf: <iframe src={contract.importedPdf} title="preview" width="100%" height="100%" />,
                            word: (
                                <iframe
                                    src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURI(
                                        url
                                    )}&ui=${uilang}&rs=${uilang}&amp;wdStartOn=1&amp;wdPrint=0&amp;wdEmbedCode=0`}
                                    title="preview"
                                    width="100%"
                                    height="100%"
                                />
                            ),
                            html: (
                                <div className="preview-container overflow-auto h-100">
                                    <div className="contract-wrapper-frontdesk">
                                        <Slate editor={editor} value={contract.elements} onChange={() => { }}>
                                            <TratoFormat
                                                document={contract}
                                                isEditable={false}
                                                BaseElementComponent={FrontDeskBaseElement}
                                                showSignatureBorder={false}
                                                showRemoveSignature={false}
                                                showPlaceholder={false}
                                            />
                                        </Slate>
                                    </div>
                                </div>
                            ),
                        }[documentType]
                    }
                </div>
            </div>
            <div>
                <div className="preview-footer py-1 justify-content-end d-flex">
                    <Nav className="list-style-none">
                        <ActionsToolBar />
                    </Nav>
                    <Button color="success" size="sm" onClick={handleReturn}>
                        <i className="fas fa-arrow-left"></i> {t('frontDesk.goback')}
                    </Button>
                </div>
            </div>
        </div>
    );
}

function FetchMoreButton({ buttonRef, onClick, disabled, fetching, hasMore }) {
    const { t } = useTranslation();

    return (
        <Button block color="white" size="sm" className="m-2" innerRef={buttonRef} onClick={onClick} disabled={disabled}>
            {fetching ? t('common.loading') : hasMore ? t('common.load_more') : t('common.loaded')}
        </Button>
    );
}

function Documents({ isLoading, selected, documents, tab }) {

    if (isLoading) return <Loading />;

    return <div>
        {documents.pages?.map((page, index) => (
            <Fragment key={index}>
                {page.docs?.map((element) => (
                    tab === 'templates' ?
                        <TemplateItem
                            template={element}
                            key={element._id}
                        /> :
                        <ContractItem
                            contract={element}
                            key={element._id}
                            isActive={element._id === selected}
                        />
                ))}
            </Fragment>
        ))}
    </div>
}

const doFetchContracts = (page, search) => fetchContractList({
    page,
    limit: 10,
    url: '/contracts/all',
    search: `namedocumentId|${search}`
})

const doFetchTemplates = (page, search) => fetchTemplates({
    page,
    search: `name|${search}`
});

const height = '100px';

function Frontdesk() {

    const methods = useFormContext();
    const { t } = useTranslation();
    const frontdeskState = useFrontDeskState();
    const dispatch = useFrontDeskDispatch();
    const { contractId } = useParams();

    const changetab = useCallback((tab) => dispatch({ type: CHANGE_TAB, tab }), [dispatch]);

    const loadMoreButtonRef = useRef();

    const { data: documents = [], isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } = useInfiniteQuery(
        [`${frontdeskState.tab}-questionnaire`, frontdeskState.search],
        ({ pageParam = 1 }) =>
            fetcher(
                frontdeskState.tab === 'contracts' ?
                    doFetchContracts(pageParam, frontdeskState.search) :
                    doFetchTemplates(pageParam, frontdeskState.search)
            ),
        {
            enabled: !!frontdeskState.tab,
            refetchOnWindowFocus: false,
            getNextPageParam: ({ pages, page }) => (Number(page) < Number(pages) ? Number(page) + 1 : false),
        }
    );

    useIntersectionObserver({
        target: loadMoreButtonRef,
        onIntersect: fetchNextPage,
        enabled: hasNextPage,
    });


    const toggleContractDetail = useCallback(() => {
        dispatch({ type: TOGGLE_SET_DETAIL_OPEN });
    }, [dispatch]);

    useQuery(['contract', contractId], () => fetcher(fetchContract(contractId)), {
        enabled: !!contractId,
        refetchOnWindowFocus: false,
        onSuccess: (contract) => {
            const computedVariables = contract.participants.reduce((obj, p) => {
                p.variables.forEach(v => {
                    obj[v.apiName] = v.type === 'date' ? v.dateValue : v.value;
                })

                return obj
            }, {})
            methods.reset({
                ...contract, computedVariables,
                ...contract.participants.reduce((obj, part, i) => {
                    agendaFields.map(field => {
                        obj[`participants[${i}].agendaProperties.${field.propertyName}`] = part.agendaProperties?.['field.propertyName'] || '';
                    })

                    return obj;
                }, {})
            });
            // methods.reset({ ...contract });
            dispatch({ type: SET_CONTRACT, contract })
        },
    });

    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 500);

    const handleSearch = (e) => {
        const target = e.target;
        setSearch(target.value)
    }

    useEffect(() => {
        dispatch({ type: SET_SEARCH, search: debouncedSearch })
    }, [debouncedSearch, dispatch])

    return (
        <div className="overflow-hidden d-flex flex-column h-100">
            <MainHeader title="frontDesk.applications">
            </MainHeader>
            <div className="d-flex flex-column h-100 overflow-hidden mb-2">
                <Row className="h-100">
                    <Col md="5" xs="12" sm="5" lg="5" xl="3" className="h-100">
                        <div style={{ height }}>
                            <Nav tabs fill>
                                <NavItem>
                                    <NavLink
                                        className={clsx({ active: frontdeskState.tab === 'templates' })}
                                        onClick={() => {
                                            changetab('templates');
                                        }}
                                    >
                                        {t('frontDesk.templates')}
                                    </NavLink>
                                </NavItem>
                                <NavItem>
                                    <NavLink
                                        className={clsx({ active: frontdeskState.tab === 'contracts' })}
                                        onClick={() => {
                                            changetab('contracts');
                                        }}
                                    >
                                        {t('frontDesk.generated')}
                                    </NavLink>
                                </NavItem>
                            </Nav>
                            <div className="my-2">
                                <input className="form-control" placeholder={t('common.search')} onChange={handleSearch}></input>
                            </div>
                        </div>
                        <div className="overflow-x-hidden overflow-y-auto p-3" style={{ height: `calc(100% - ${height})` }}>

                            <Documents
                                documents={documents}
                                isLoading={isLoading}
                                tab={frontdeskState.tab}
                                selected={frontdeskState.contractId}
                            />

                            <FetchMoreButton
                                buttonRef={loadMoreButtonRef}
                                onClick={() => fetchNextPage()}
                                disabled={!hasNextPage || isFetchingNextPage}
                                fetching={isFetchingNextPage}
                                hasMore={hasNextPage}
                            />
                        </div>
                    </Col>

                    <Col md="7" xs="12" sm="7" lg="7" xl="9" className="h-100 overflow-hidden">
                        {contractId &&
                            <EditorProvider mode="frontdesk" documentId={contractId}>
                                <PreviewMode />
                            </EditorProvider>
                        }
                        <Contract />
                        <Template />
                    </Col>
                </Row>
                <ContractDetailContainer
                    isOpen={frontdeskState.isContractDetailOpen}
                    toggle={toggleContractDetail}
                    initialContract={frontdeskState.contract}
                />
            </div>
        </div>
    );
}

function FrontdeskContainer() {
    return (
        <FrontDeskProvider >
            <Frontdesk></Frontdesk>
        </FrontDeskProvider>
    );
}

export default FrontdeskContainer;
